import React, { useState } from 'react';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { showToast } from '../utils/toast';
import { baseUrl, extractErrorMessage } from '../Services/apiService';
import { handleAPIError } from '../utils/errorHandler';
import axios from 'axios';
import FormField from './FormField';
import { PhoneInput } from "react-international-phone";
import "react-international-phone/style.css";
import { validateName, validateEmail, validatePhone, validateRole } from '../utils/formValidation';
import { validatePhoneRealTime } from '../utils/phoneValidation';

const CreateUserModal = ({ open, onClose, onSubmit }) => {
  const { createUser, users } = useData();
  const { user: currentUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: ''
  });
  const [selectedCountry, setSelectedCountry] = useState('in'); // Track selected country for validation

  // Validation states
  const [nameValidation, setNameValidation] = useState({ isValid: true, message: '' });
  const [emailValidation, setEmailValidation] = useState({ isValid: true, message: '' });
  const [phoneValidation, setPhoneValidation] = useState({ isValid: true, message: '' });
  const [roleValidation, setRoleValidation] = useState({ isValid: true, message: '' });
  
  // Track if fields have been touched
  const [phoneTouched, setPhoneTouched] = useState(false);

  // Validation handlers
  const handleNameChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, name: value }));
    setNameValidation(validateName(value, 'Full name'));
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, email: value }));
    setEmailValidation(validateEmail(value));
  };

  const handlePhoneChange = (value, country, metadata) => {
    // react-international-phone onChange signature: (value, country, metadata)
    // value: phone number in international format (e.g., "+919876543210")
    // country: { countryCode: 'IN', dialCode: '+91', ... }
    // metadata: additional metadata
    
    setFormData(prev => ({ ...prev, phone: value }));
    setPhoneTouched(true);
    
    // Update selected country for validation
    if (country?.countryCode) {
      setSelectedCountry(country.countryCode.toLowerCase());
    }
    
    // Real-time validation with country-specific feedback
    const countryCode = country?.countryCode || selectedCountry?.toUpperCase() || null;
    const validation = validatePhoneRealTime(value, countryCode);
    setPhoneValidation(validation);
  };

  const handleRoleChange = (value) => {
    setFormData(prev => ({ ...prev, role: value }));
    setRoleValidation(validateRole(value));
  };

  const roles = [
    { value: 'employee', label: 'Employee' },
    { value: 'business_head', label: 'Business Head' },
    { value: 'vendor', label: 'Vendor' }
    // Admin role cannot be assigned via UI - system managed only
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Ensure phone number is in international format with + prefix
      const formPhone = formData.phone.startsWith('+') ? formData.phone : `+${formData.phone}`;

      // Validate all fields
      const nameValidationResult = validateName(formData.name, 'Full name');
      const emailValidationResult = validateEmail(formData.email);
      const phoneValidationResult = validatePhone(formPhone); // Use full international format
      const roleValidationResult = validateRole(formData.role);

      if (!nameValidationResult.isValid) {
        setNameValidation(nameValidationResult);
        showToast.error(nameValidationResult.message);
        setIsLoading(false);
        return;
      }

      if (!emailValidationResult.isValid) {
        setEmailValidation(emailValidationResult);
        showToast.error(emailValidationResult.message);
        setIsLoading(false);
        return;
      }

      if (!phoneValidationResult.isValid) {
        setPhoneValidation(phoneValidationResult);
        setPhoneTouched(true);
        showToast.error(phoneValidationResult.message);
        setIsLoading(false);
        return;
      }

      if (!roleValidationResult.isValid) {
        setRoleValidation(roleValidationResult);
        showToast.error(roleValidationResult.message);
        setIsLoading(false);
        return;
      }

      // Check for duplicate email
      const existingUserByEmail = users.find(user => user.email.toLowerCase() === formData.email.toLowerCase());
      if (existingUserByEmail) {
        setEmailValidation({ isValid: false, message: 'Email already exists' });
        showToast.error('Email already exists');
        setIsLoading(false);
        return;
      }

      // Check for duplicate phone number (compare normalized international format)
      const existingUserByPhone = users.find(user => {
        if (!user.phone_number) return false;
        // Normalize both numbers for comparison (ensure + prefix)
        const userPhone = user.phone_number.startsWith('+') ? user.phone_number : `+${user.phone_number}`;
        const normalizedUserPhone = userPhone.replace(/\D/g, '');
        const normalizedFormPhone = formPhone.replace(/\D/g, '');
        return normalizedUserPhone === normalizedFormPhone;
      });

      if (existingUserByPhone) {
        setPhoneValidation({ isValid: false, message: 'Phone number already exists' });
        setPhoneTouched(true);
        showToast.error('Phone number already exists');
        setIsLoading(false);
        return;
      }

      const userData = {
        full_name: formData.name.trim(),
        email: formData.email.toLowerCase().trim(),
        phone_number: formPhone, // Store full international format with + prefix
        role: formData.role,
      };

      // Use the onSubmit prop if provided, otherwise use the old method
      if (onSubmit) {
        await onSubmit(userData);
      } else {
        // Fallback to direct API call
        const response = await axios.post(`${baseUrl}/users/`, userData, {
          headers: {
            'Authorization': `Bearer ${currentUser.token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.data) {
          // Also add to local state for immediate UI update
          createUser({
            id: response.data.id,
            name: response.data.full_name,
            email: response.data.email,
            phone: response.data.phone_number,
            role: response.data.role,
          });
          showToast.success('User created successfully!');
        }
      }
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        role: '',
      });
      setSelectedCountry('in');
      
      // Reset validation states
      setNameValidation({ isValid: true, message: '' });
      setEmailValidation({ isValid: true, message: '' });
      setPhoneValidation({ isValid: true, message: '' });
      setRoleValidation({ isValid: true, message: '' });
      setPhoneTouched(false);
      
      onClose();
    } catch (error) {
      // Check if error is related to phone number
      const errorMessage = error?.response?.data?.message || error?.message || '';
      const phoneErrorKeywords = ['phone', 'phone_number', 'duplicate', 'already exists'];
      const isPhoneError = phoneErrorKeywords.some(keyword => 
        errorMessage.toLowerCase().includes(keyword.toLowerCase())
      );

      if (isPhoneError) {
        setPhoneValidation({ isValid: false, message: 'Phone number already exists' });
        setPhoneTouched(true);
        showToast.error('Phone number already exists');
      } else {
        handleAPIError(error, 'Failed to create user', true);
      }
      // Don't close modal on error - let user fix the issue
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      role: '',
    });
    
    // Reset validation states
    setNameValidation({ isValid: true, message: '' });
    setEmailValidation({ isValid: true, message: '' });
    setPhoneValidation({ isValid: true, message: '' });
    setRoleValidation({ isValid: true, message: '' });
    setPhoneTouched(false);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px] w-[95vw] sm:h-auto h-auto ">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-yellow-600" />
            Create New User
          </DialogTitle>
          
        </DialogHeader>

        <form onSubmit={handleSubmit} className="sm:space-y-6 space-y-2">
          <div className="grid grid-cols-1 sm:gap-3  gap-2">
            {/* Name */}
            <FormField
              label="Full Name"
              type="text"
              value={formData.name}
              onChange={handleNameChange}
              validation={nameValidation} 
              placeholder="Enter Full Name"
              required
            />

            {/* Email */}
            <FormField
              label="Email Address"
              type="email"
              value={formData.email}
              onChange={handleEmailChange}
              validation={emailValidation}
              placeholder="user@company.com"
              required
            />

            <div className="space-y-0 flex  gap-1 flex-col w-full">
              <label htmlFor="phone" className="text-sm font-medium">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <div className="w-full">
                <PhoneInput
                  defaultCountry="in"
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  forceDialCode={true}
                  className='w-full'
                  inputStyle={{ width: "100%", height: "36px" }}
                  hideDropdown={false}
                  disableCountryGuess={false}
                />
              </div>
              {/* Real-time validation feedback */}
              {phoneTouched && (
                <div className="mt-1 space-y-1">
                  {phoneValidation.isValid && (
                    <div className="flex items-center gap-2">
                      <p className="text-sm text-green-600 font-medium">
                        {phoneValidation.message}
                      </p>
                    </div>
                  ) }
                  
                </div>
              )}
            </div>
          

            {/* Role */}
            <FormField
              label="Role"
              type="select"
              value={formData.role}
              onChange={handleRoleChange}
              validation={roleValidation}
              placeholder="Select a Role"
              required
              options={roles}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="flex-1 sm:flex-initial"
              data-testid="cancel-user-btn"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading || !phoneValidation.isValid || !nameValidation.isValid || !emailValidation.isValid || !roleValidation.isValid}
              className="btn-gold flex-1 sm:flex-initial disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="create-user-submit-btn"
            >
              {isLoading ? 'Creating...' : 'Create User'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateUserModal;